import { type UseQueryResult } from '@tanstack/react-query';
import { ROUTE_QUIZ_GOALS } from '@hungryroot/routes';
import { QUIZ_NAVIGATION_LOCAL_STORAGE_KEY } from '../defs/localStorage';
import { EatingHabit, type Quiz } from '../defs/quiz';
import {
  ROUTE_QUIZ_HELLO,
  ROUTE_MACRO_NUTRIENT,
  ROUTE_MACRO_NUTRIENT_LOW_SODIUM,
  ROUTE_QUIZ_INTRO,
  STARTING_ROUTE,
  type AllQuizRoutes,
} from '../defs/routes';
import { hasMetQuizHelloRequirements } from '../utils/hasMetQuizHelloRequirements';

interface RedirectParams<T extends string> {
  nullQuizRedirectPath?: AllQuizRoutes;
  loc: T | undefined;
  searchParams?: string;
  query: Pick<UseQueryResult<Quiz | null>, 'data' | 'isFetching'>;
  quizRoutes: readonly AllQuizRoutes[];
}

export function getRedirectPath<T extends string>(
  params: RedirectParams<T>
): AllQuizRoutes | null {
  const {
    loc,
    searchParams,
    query,
    nullQuizRedirectPath = STARTING_ROUTE,
    quizRoutes,
  } = params;

  const { isFetching, data: quiz } = query;

  const isStartingRouteIntro = STARTING_ROUTE === ROUTE_QUIZ_INTRO;
  const firstQuizQuestionPage = isStartingRouteIntro
    ? quizRoutes[1]
    : STARTING_ROUTE;

  if (loc === undefined) {
    return firstQuizQuestionPage;
  }

  // If isFetching is true
  // ReactQuery is refreshing our data,
  // and the data property is out of date
  // We should never redirect in this case.
  // Wait for the request to settle.
  if (isFetching === true) {
    return null;
  }

  if (quiz === undefined || loc === undefined) {
    return null;
  }

  // Redirect logic for exp 157 variant 2 homepage quiz question + exp 152
  // Exp 157 direct to quiz question redirects to ROUTE_QUIZ_HELLO
  // Exp 152 direct to quiz question redirects to ROUTE_QUIZ_GOALS. Direct to quiz question rendered on homepage only if exp 157 variant 2 wins.
  const queryParams = new URLSearchParams(searchParams);
  const habitsParam = queryParams.get('habits');

  if (
    quiz === null &&
    (loc === ROUTE_QUIZ_HELLO || loc === ROUTE_QUIZ_GOALS) &&
    habitsParam !== null
  ) {
    return null;
  }

  // If user does not have a persisted quiz
  // AND user is not on intro screen
  // Redirect to intro
  if (quiz === null) {
    if (loc !== nullQuizRedirectPath) {
      return nullQuizRedirectPath;
    }
    return null;
  }

  const helloIndex = quizRoutes.indexOf(ROUTE_QUIZ_HELLO);
  const routesBeforeRequiredHouseholdSize = quizRoutes.filter(
    (r, i) => isStartingRouteIntro && r !== ROUTE_QUIZ_INTRO && i <= helloIndex
  );
  const isLocBeforeRequiredHouseholdSize =
    routesBeforeRequiredHouseholdSize.some((route) => route === loc);

  // Handles Plan Recommender Control + Variant
  const quizHelloRequirementsMet = hasMetQuizHelloRequirements(quiz);

  // If user has a persisted quiz
  // AND user has not selected a household size
  // AND user is not on a page after household size is required
  // Redirect to first question page
  if (quizHelloRequirementsMet === false && !isLocBeforeRequiredHouseholdSize) {
    return firstQuizQuestionPage;
  }

  // If user has persisted quiz
  // AND user is on the intro screen
  // Redirect to first question page
  if (quizHelloRequirementsMet && loc === ROUTE_QUIZ_INTRO) {
    return firstQuizQuestionPage;
  }

  // This will happen if the user has selected low sodium on the main macro nutrient page
  // and then removes it on the low sodium route on that page.
  // In that case, we want to redirect back to the main macro nutrient page.
  if (
    loc === ROUTE_MACRO_NUTRIENT_LOW_SODIUM &&
    !quizRoutes.includes(ROUTE_MACRO_NUTRIENT_LOW_SODIUM)
  ) {
    return ROUTE_MACRO_NUTRIENT;
  }

  return null;
}

export function shouldExcludeMeatRoute(habits: Quiz['habits']) {
  const excludeSeafoodRoute = shouldExcludeSeafoodRoute(habits);
  if (excludeSeafoodRoute === true) {
    return excludeSeafoodRoute;
  }

  return habits === EatingHabit.Pescatarian;
}

export function shouldExcludeSeafoodRoute(habits: Quiz['habits']) {
  return habits === EatingHabit.Vegan || habits === EatingHabit.Vegetarian;
}

export function skipQuizRoutes<T extends string>(
  allQuizRoutes: readonly T[],
  skipRoutes: Partial<Record<T, boolean>>
) {
  const skipRouteKeys = Object.keys(skipRoutes) as T[];
  const removeRoutes = skipRouteKeys.filter(
    (route) => skipRoutes[route] === true
  );

  return allQuizRoutes.filter((route) => !removeRoutes.includes(route));
}

export function setSavedNavState<T extends string>(
  pathname: T | '/survey/plan/'
) {
  const data = {
    pathname,
    timestamp: new Date(),
  };
  localStorage.setItem(QUIZ_NAVIGATION_LOCAL_STORAGE_KEY, JSON.stringify(data));
}
